import {
  documentUrl,
  get,
  observationLetterUrl,
  post,
  reviewReportUrl,
  submissionPdfUrl,
  upload,
  type ApiResult
} from './client';
import type {
  AssignmentRow,
  CoInvestigatorRow,
  CommitteeInboxRow,
  CommitteeProposalRow,
  CoverLetterRow,
  DocumentRow,
  GanttRow,
  HistoryRow,
  PortalConfig,
  ProposalBodyRow,
  ProposalHeaderRow,
  ProposalSummaryRow,
  ReadinessIssue,
  ResearcherProfile,
  ReviewCommentRow,
  ReviewQuestionRow,
  ReviewQueueRow,
  ReviewSectionRow,
  ReviewSummaryAnswerRow,
  ReviewSummaryCommentRow,
  ReviewSummaryReviewerRow,
  ReviewerRow,
  ReviewerWorkItemRow,
  ReviewerWorkloadRow,
  StatusCountRow,
  SubmissionRow,
  ActivityRow,
  WorkBucketRow,
  WorkItemRow
} from './types';

/* ------------------------------------------------------------------ config */

interface ConfigResponse {
  Success?: boolean;
  Message?: string;
  TurnstileSiteKey?: string;
  MaxDocumentMB?: number;
  Lookups?: Record<string, Record<string, unknown>[]>;
  [key: string]: unknown;
}

export async function fetchConfig(): Promise<PortalConfig> {
  const res = await get<ConfigResponse>('Config');
  const head = res.head;
  const lk = head.Lookups ?? {};

  /* Lookups is a nested DataSet: Table = envelope, Table1..4 = the data. */
  const meta = (lk.Table4?.[0] ?? {}) as Record<string, unknown>;

  return {
    turnstileSiteKey: head.TurnstileSiteKey ?? '',
    maxDocumentMB: Number(head.MaxDocumentMB ?? 1) || 1,
    researchTypes: (lk.Table1 ?? []) as unknown as PortalConfig['researchTypes'],
    documentSlots: (lk.Table2 ?? []) as unknown as PortalConfig['documentSlots'],
    statuses: (lk.Table3 ?? []) as unknown as PortalConfig['statuses'],
    maxDurationMonths: Number(meta.MaxDurationMonths ?? 24) || 24,
    irbEmail: String(meta.IrbEmail ?? 'irb@sihs.org.pk')
  };
}

/* ----------------------------------------------------------------- account */

export interface AuthResult extends ApiResult {
  sessionToken: string | null;
  pendingToken: string | null;
  profile: ResearcherProfile | null;
}

function toAuth(res: ApiResult): AuthResult {
  return {
    ...res,
    sessionToken: (res.head.SessionToken as string) ?? null,
    pendingToken: (res.head.PendingToken as string) ?? null,
    profile: (res.tables[1]?.[0] as unknown as ResearcherProfile) ?? null
  };
}

export async function register(body: {
  FullName: string;
  Email: string;
  Contact: string;
  Password: string;
  Designation: string;
  Institution: string;
  TurnstileToken: string;
}): Promise<AuthResult> {
  return toAuth(await post('Register', body));
}

export async function login(body: {
  LoginId: string;
  Password: string;
  TurnstileToken: string;
}): Promise<AuthResult> {
  return toAuth(await post('Login', body));
}

export async function verifyPin(body: { PendingToken: string; Pin: string }): Promise<AuthResult> {
  return toAuth(await post('VerifyPin', body));
}

export async function resendPin(body: {
  PendingToken: string;
  TurnstileToken: string;
}): Promise<AuthResult> {
  return toAuth(await post('ResendPin', body));
}

export async function validateSession(SessionToken: string): Promise<AuthResult> {
  return toAuth(await post('Session', { SessionToken }));
}

export async function logout(SessionToken: string): Promise<ApiResult> {
  return post('Logout', { SessionToken });
}

export async function forgotPassword(body: {
  LoginId: string;
  TurnstileToken: string;
}): Promise<AuthResult> {
  return toAuth(await post('ForgotPassword', body));
}

export async function resetPassword(body: {
  PendingToken: string;
  Pin: string;
  NewPassword: string;
}): Promise<ApiResult> {
  return post('ResetPassword', body);
}

export async function changePassword(body: {
  SessionToken: string;
  CurrentPassword: string;
  NewPassword: string;
}): Promise<ApiResult> {
  return post('ChangePassword', body);
}

/* ---------------------------------------------------------------- proposal */

export async function myProposals(SessionToken: string) {
  const res = await post('MyProposals', { SessionToken });
  return { ...res, rows: (res.tables[1] ?? []) as unknown as ProposalSummaryRow[] };
}

export async function startProposal(SessionToken: string) {
  const res = await post('StartProposal', { SessionToken });
  return { ...res, researchCode: Number(res.head.ResearchCode ?? 0) };
}

export interface FullProposal {
  canEdit: boolean;
  header: ProposalHeaderRow | null;
  body: ProposalBodyRow | null;
  typeIds: number[];
  gantt: GanttRow[];
  submission: SubmissionRow | null;
  coInvestigators: CoInvestigatorRow[];
  documents: DocumentRow[];
  history: HistoryRow[];
  /** Researcher's own copy only; the committee view does not carry it. */
  coverLetter?: CoverLetterRow | null;
}

export async function getProposal(SessionToken: string, ResearchCode: number) {
  const res = await post('GetProposal', { SessionToken, ResearchCode });
  const t = res.tables;

  const proposal: FullProposal = {
    canEdit: res.head.CanEdit === true,
    header: (t[1]?.[0] as unknown as ProposalHeaderRow) ?? null,
    body: (t[2]?.[0] as unknown as ProposalBodyRow) ?? null,
    typeIds: ((t[3] ?? []) as { ResTypeId: number }[]).map((r) => Number(r.ResTypeId)),
    gantt: (t[4] ?? []) as unknown as GanttRow[],
    submission: (t[5]?.[0] as unknown as SubmissionRow) ?? null,
    coInvestigators: (t[6] ?? []) as unknown as CoInvestigatorRow[],
    documents: (t[7] ?? []) as unknown as DocumentRow[],
    history: (t[8] ?? []) as unknown as HistoryRow[],
    /* Table 9 is the legacy participant consent block, which the wizard no
       longer collects. */
    coverLetter: (t[10]?.[0] as unknown as CoverLetterRow) ?? null
  };

  return { ...res, proposal };
}

export type SaveProposalBody = {
  SessionToken: string;
  ResearchCode: number;
} & Partial<{
  Title: string;
  ReviewCategory: string;
  ProjectTypeIds: string;
  OtherTypeReason: string;
  StartDate: string;
  DurationMonths: string;
  Introduction: string;
  Rationale: string;
  Objectives: string;
  StudyType: string;
  SampleSize: string;
  SamplingTech: string;
  StudyPopulation: string;
  Inclusion: string;
  ExclCriteria: string;
  StudyInstrument: string;
  ProcedureDet: string;
  DataAnalysis: string;
  ReferencesDet: string;
  Facilities: string;
  PotentialBenefits: string;
  PotentialRisks: string;
  Ethical: string;
  Dissemination: string;
}>;

export async function saveProposal(body: SaveProposalBody) {
  return post('SaveProposal', body);
}

export async function saveGantt(body: {
  SessionToken: string;
  ResearchCode: number;
  Activities: string;
}) {
  return post('SaveGantt', body);
}

export async function saveSubmission(body: {
  SessionToken: string;
  ResearchCode: number;
  GrantRequired?: boolean | null;
  GrantAmount?: number | null;
  PiName?: string;
  PiTitle?: string;
  PiInstitution?: string;
  PiContact?: string;
  PiEmail?: string;
  HodName?: string;
  SupervisorName?: string;
  CoInvestigators?: string;
}) {
  return post('SaveSubmission', body);
}

/** The cover letter a sent-back proposal is resubmitted with (PDF). */
export async function uploadCoverLetter(SessionToken: string, ResearchCode: number, file: File) {
  const form = new FormData();
  form.append('SessionToken', SessionToken);
  form.append('ResearchCode', String(ResearchCode));
  form.append('file', file);
  return upload('UploadCoverLetter', form);
}

export async function checkReadiness(SessionToken: string, ResearchCode: number) {
  const res = await post('CheckReadiness', { SessionToken, ResearchCode });
  return {
    ...res,
    isReady: res.head.IsReady === true,
    issues: (res.tables[1] ?? []) as unknown as ReadinessIssue[]
  };
}

export async function submitProposal(SessionToken: string, ResearchCode: number) {
  const res = await post('Submit', { SessionToken, ResearchCode });
  return {
    ...res,
    irbNo: Number(res.head.IrbNo ?? 0),
    statusId: String(res.head.StatusId ?? ''),
    submissionPdf: (res.head.SubmissionPdf as string) ?? null,
    /* One sentence per uploaded document that could not be included. When this
       is non-empty nothing was submitted — the researcher fixes those files and
       tries again. */
    documentProblems: (res.head.DocumentProblems as string[] | undefined) ?? []
  };
}

/* --------------------------------------------------------------- documents */

export async function uploadDocument(
  SessionToken: string,
  ResearchCode: number,
  DocCode: number,
  file: File
) {
  const form = new FormData();
  form.append('SessionToken', SessionToken);
  form.append('ResearchCode', String(ResearchCode));
  form.append('DocCode', String(DocCode));
  form.append('file', file);
  return upload('UploadDocument', form);
}

export async function deleteDocument(
  SessionToken: string,
  ResearchCode: number,
  DocCode: number
) {
  return post('DeleteDocument', { SessionToken, ResearchCode, DocCode });
}

export { documentUrl, observationLetterUrl, reviewReportUrl, submissionPdfUrl };

/* --------------------------------------------------------------- committee */

export async function committeeDashboard(SessionToken: string) {
  const res = await post('Committee/Dashboard', { SessionToken });
  return {
    ...res,
    actorName: String(res.head.ActorName ?? ''),
    isSrcAdmin: res.head.IsSrcAdmin === true,
    isIrbAdmin: res.head.IsIrbAdmin === true,
    isReviewer: res.head.IsReviewer === true,
    counts: (res.tables[1] ?? []) as unknown as StatusCountRow[],
    inbox: ((res.tables[2]?.[0] ?? null) as unknown as CommitteeInboxRow) ?? null
  };
}

/** What this office has to act on, grouped by the action it is waiting for. */
export async function committeeWorkQueue(SessionToken: string) {
  const res = await post('Committee/WorkQueue', { SessionToken });
  return {
    ...res,
    actorName: String(res.head.ActorName ?? ''),
    isSrcAdmin: res.head.IsSrcAdmin === true,
    isIrbAdmin: res.head.IsIrbAdmin === true,
    isReviewer: res.head.IsReviewer === true,
    buckets: (res.tables[1] ?? []) as unknown as WorkBucketRow[],
    items: (res.tables[2] ?? []) as unknown as WorkItemRow[],
    activity: (res.tables[3] ?? []) as unknown as ActivityRow[]
  };
}

export async function committeeProposals(body: {
  SessionToken: string;
  StatusId?: string | null;
  Search?: string | null;
  Page?: number;
  PageSize?: number;
}) {
  const res = await post('Committee/Proposals', body);
  return {
    ...res,
    total: Number(res.head.TotalRows ?? 0),
    rows: (res.tables[1] ?? []) as unknown as CommitteeProposalRow[]
  };
}

export interface CommitteeProposalDetail extends FullProposal {
  isSrcAdmin: boolean;
  isIrbAdmin: boolean;
  isAssignedReviewer: boolean;
  researcherName: string;
  researcherEmail: string;
  assignments: AssignmentRow[];
  typeNames: string[];
}

export async function committeeProposal(SessionToken: string, ResearchCode: number) {
  const res = await post('Committee/Proposal', { SessionToken, ResearchCode });
  const t = res.tables;
  const header = (t[1]?.[0] ?? null) as unknown as
    | (ProposalHeaderRow & { ResearcherName: string; ResearcherEmail: string })
    | null;

  const detail: CommitteeProposalDetail = {
    canEdit: false,
    isSrcAdmin: res.head.IsSrcAdmin === true,
    isIrbAdmin: res.head.IsIrbAdmin === true,
    isAssignedReviewer: res.head.IsAssignedReviewer === true,
    header,
    researcherName: header?.ResearcherName ?? '',
    researcherEmail: header?.ResearcherEmail ?? '',
    body: (t[2]?.[0] as unknown as ProposalBodyRow) ?? null,
    typeIds: ((t[3] ?? []) as { ResTypeId: number }[]).map((r) => Number(r.ResTypeId)),
    typeNames: ((t[3] ?? []) as { Description: string }[]).map((r) => String(r.Description)),
    gantt: (t[4] ?? []) as unknown as GanttRow[],
    submission: (t[5]?.[0] as unknown as SubmissionRow) ?? null,
    coInvestigators: (t[6] ?? []) as unknown as CoInvestigatorRow[],
    documents: (t[7] ?? []) as unknown as DocumentRow[],
    history: (t[8] ?? []) as unknown as HistoryRow[],
    assignments: (t[9] ?? []) as unknown as AssignmentRow[]
  };

  return { ...res, detail };
}

export async function forwardProposal(body: {
  SessionToken: string;
  ResearchCode: number;
  Remarks?: string;
}) {
  return post('Committee/Forward', body);
}

/**
 * Returns the proposal for correction. The observation letter (PDF) is
 * required, as it was on Sendback.aspx; it is merged in front of the proposal
 * when the researcher resubmits.
 */
export async function sendBackProposal(body: {
  SessionToken: string;
  ResearchCode: number;
  Remarks: string;
  ObservationLetter: File;
}) {
  const form = new FormData();
  form.append('SessionToken', body.SessionToken);
  form.append('ResearchCode', String(body.ResearchCode));
  form.append('Remarks', body.Remarks);
  form.append('file', body.ObservationLetter);
  return upload('Committee/SendBack', form);
}

export async function decideProposal(body: {
  SessionToken: string;
  ResearchCode: number;
  Approved: boolean;
  Remarks?: string;
}) {
  return post('Committee/Decide', body);
}

export async function listReviewers(SessionToken: string, ResearchCode: number) {
  const res = await post('Committee/Reviewers', { SessionToken, ResearchCode });
  return { ...res, rows: (res.tables[1] ?? []) as unknown as ReviewerRow[] };
}

export async function assignReviewers(body: {
  SessionToken: string;
  ResearchCode: number;
  ReviewerIds: string;
  DueDate?: string;
}) {
  return post('Committee/AssignReviewers', body);
}

export async function reviewSummary(SessionToken: string, ResearchCode: number) {
  const res = await post('Committee/ReviewSummary', { SessionToken, ResearchCode });
  return {
    ...res,
    reviewers: (res.tables[1] ?? []) as unknown as ReviewSummaryReviewerRow[],
    answers: (res.tables[2] ?? []) as unknown as ReviewSummaryAnswerRow[],
    comments: (res.tables[3] ?? []) as unknown as ReviewSummaryCommentRow[]
  };
}

/* ---------------------------------------------------------------- reviewer */

/** A reviewer's workload: the totals plus their assignments, most urgent first. */
export async function reviewerWorkQueue(SessionToken: string, IncludeDone = false) {
  const res = await post('Reviewer/WorkQueue', { SessionToken, IncludeDone });
  return {
    ...res,
    actorName: String(res.head.ActorName ?? ''),
    workload: ((res.tables[1]?.[0] ?? null) as unknown as ReviewerWorkloadRow) ?? null,
    rows: (res.tables[2] ?? []) as unknown as ReviewerWorkItemRow[]
  };
}

export async function reviewerQueue(SessionToken: string, IncludeDone = false) {
  const res = await post('Reviewer/Queue', { SessionToken, IncludeDone });
  return { ...res, rows: (res.tables[1] ?? []) as unknown as ReviewQueueRow[] };
}

export async function getReview(body: {
  SessionToken: string;
  ResearchCode: number;
  ReviewerId?: number | null;
}) {
  const res = await post('Reviewer/GetReview', body);
  return {
    ...res,
    canEdit: res.head.CanEdit === true,
    isReviewed: res.head.IsReviewed === true,
    sections: (res.tables[1] ?? []) as unknown as ReviewSectionRow[],
    questions: (res.tables[2] ?? []) as unknown as ReviewQuestionRow[],
    comments: (res.tables[3] ?? []) as unknown as ReviewCommentRow[]
  };
}

export async function saveReview(body: {
  SessionToken: string;
  ResearchCode: number;
  Answers: string;
  Comments: string;
  Submit: boolean;
}) {
  const res = await post('Reviewer/SaveReview', body);
  return { ...res, allReviewsIn: res.head.AllReviewsIn === true };
}
